import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CrudMode, CrudSchemaFields, FormItemValueType, type EnsoFormItem } from '@ohpen/enso-ui';
import { Button, Form, Input, Space, Tag, Tooltip } from 'antd';
import { ExportOutlined, UserAddOutlined } from '@ant-design/icons';
import { IconBuilding, IconFileText } from '@tabler/icons-react';
import dayjs from 'dayjs';

import { CognitoUserStatus, FieldType, UserRoleType } from '@pmp/api';
import { DEFAULT_TEXT_MAX_LENGTH_SHORT, textLimitProps } from '@pmp/utils';
import { getUserStatusTags, renderReadonlyStatusTag } from '@pmp/components';
import { TabProps } from '../interfaces';
import { getBrokerHighlightProps, isRoleOptionDisabled } from '../utils';
import { CrudFormCard } from '../../firms/components/CrudFormCard';
import { Guard } from '@ohpen/react-authz';
import styles from '../styles.module.css';

export const PersonalDetailsTab: React.FC<TabProps> = ({
  form,
  mode,
  selectsOptions,
  fcaModifiedFields,
  populateFcaData,
  removeFcaModifiedField,
  inviteIntermediary,
}) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;

  const watchedRoles = Form.useWatch('roles', form);
  const watchedIrn = Form.useWatch('irn', form);
  const watchedCognitoUserStatus = Form.useWatch('cognitoUserStatus', form);

  const [loading, setLoading] = useState<boolean>(false);
  const [inviting, setInviting] = useState<boolean>(false);

  const sendInvitation = useCallback(() => {
    if (!inviteIntermediary) return;
    setInviting(true);
    inviteIntermediary().finally(() => setInviting(false));
  }, [inviteIntermediary]);

  const populateData = useCallback(() => {
    setLoading(true);
    if (populateFcaData && watchedIrn) {
      populateFcaData(watchedIrn).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [populateFcaData, watchedIrn]);

  const roleOptions = useMemo(() => {
    const base = selectsOptions[FieldType.USER_ROLE_TYPE] ?? [];
    const selected = (watchedRoles as UserRoleType[] | undefined) ?? [];
    return base.map(opt => ({
      ...opt,
      disabled: isRoleOptionDisabled(opt.value as UserRoleType, selected),
    }));
  }, [selectsOptions, watchedRoles]);

  const registrationColumns: EnsoFormItem[] = useMemo(
    () => [
      {
        title: t('lender.brokers.labels.currentStatus'),
        dataIndex: 'userStatus',
        valueType: 'switch',
        readonly,
        renderReadonly: renderReadonlyStatusTag(getUserStatusTags(t)),
        formItemProps: {
          initialValue: false,
        },
        fieldProps: {
          checkedChildren: t('lender.brokers.labels.active'),
          unCheckedChildren: t('lender.brokers.labels.inactive'),
        },
        colProps: { span: 24 },
      },
      ...(mode !== CrudMode.CREATE
        ? [
            {
              valueType: FormItemValueType.group,
              colProps: { span: 24 },
              columns: [
                {
                  title: t('lender.brokers.labels.registrationStatus'),
                  dataIndex: 'cognitoUserStatus',
                  valueType: 'select' as const,
                  readonly: true,
                  readonlyDisplay: 'tag' as const,
                  fieldProps: {
                    options: [
                      {
                        label: t(`lender.brokers.cognitoUserStatus.${CognitoUserStatus.NOT_CREATED}`),
                        value: CognitoUserStatus.NOT_CREATED,
                      },
                      { label: t(`lender.brokers.cognitoUserStatus.${CognitoUserStatus.CONFIRMED}`), value: CognitoUserStatus.CONFIRMED },
                      {
                        label: t(`lender.brokers.cognitoUserStatus.${CognitoUserStatus.RESET_REQUIRED}`),
                        value: CognitoUserStatus.RESET_REQUIRED,
                      },
                      {
                        label: t(`lender.brokers.cognitoUserStatus.${CognitoUserStatus.FORCE_CHANGE_PASSWORD}`),
                        value: CognitoUserStatus.FORCE_CHANGE_PASSWORD,
                      },
                    ],
                    tagColorsMapping: {
                      [CognitoUserStatus.NOT_CREATED]: 'default',
                      [CognitoUserStatus.CONFIRMED]: 'success',
                      [CognitoUserStatus.RESET_REQUIRED]: 'error',
                      [CognitoUserStatus.FORCE_CHANGE_PASSWORD]: 'volcano',
                    },
                  },
                  colProps: { span: 12 },
                },
                {
                  title: ' ',
                  dataIndex: 'inviteIntermediary',
                  key: 'inviteIntermediary',
                  valueType: 'text' as const,
                  colProps: { span: 12 },
                  renderFormItem: () => {
                    return (
                      <Guard permissions={['brokers:update']} flags={['enableBrokersCrud']}>
                        <Tooltip
                          key="invite-intermediary"
                          title={t(`lender.brokers.cognitoUserStatusTooltip.${watchedCognitoUserStatus}`)}
                          placement="top"
                        >
                          <Button
                            icon={<UserAddOutlined />}
                            type="primary"
                            loading={inviting}
                            onClick={sendInvitation}
                            disabled={
                              readonly ||
                              watchedCognitoUserStatus === CognitoUserStatus.CONFIRMED ||
                              watchedCognitoUserStatus === CognitoUserStatus.RESET_REQUIRED
                            }
                          >
                            {t('lender.brokers.labels.inviteIntermediary')}
                          </Button>
                        </Tooltip>
                      </Guard>
                    );
                  },
                },
              ],
            },
          ]
        : []),
      {
        valueType: FormItemValueType.group,
        columns: [
          {
            title: t('lender.brokers.labels.roles'),
            dataIndex: 'roles',
            valueType: FormItemValueType.select,
            readonly,
            readonlyDisplay: 'tag',
            fieldProps: {
              mode: 'multiple',
              tagColor: 'blue',
              options: roleOptions,
            },
            formItemProps: {
              rules: [{ required: true }],
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: FormItemValueType.group,
        colProps: { span: 24 },
        columns: [
          {
            title: t('lender.brokers.labels.irn'),
            dataIndex: 'irn',
            valueType: FormItemValueType.text,
            readonly,
            renderFormItem: () => (
              <Space.Compact block>
                <Form.Item name="irn" noStyle>
                  <Input {...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT)} disabled={mode !== CrudMode.CREATE} />
                </Form.Item>
                <Button type="primary" onClick={populateData} disabled={readonly} loading={loading}>
                  {t('firms.buttons.populateData')}
                </Button>
              </Space.Compact>
            ),
            formItemProps: {
              tooltip: readonly ? null : t('lender.brokers.labels.irnPopulateTooltip'),
            },
            colProps: { span: 12 },
          },
          {
            title: t('firms.labels.fcaRegister'),
            dataIndex: 'fcaRegisterLink',
            key: 'fcaRegisterLink',
            valueType: 'text' as const,
            colProps: { span: 12 },
            renderFormItem: () => {
              const irn = form?.getFieldValue('irn');
              if (!irn) return '-';
              const url = `https://register.fca.org.uk/s/search?q=${encodeURIComponent(irn)}&type=Individuals`;
              return (
                <a href={url} target="_blank" rel="noreferrer">
                  {t('firms.labels.goToWebsite')}
                  <ExportOutlined />
                </a>
              );
            },
          },
        ],
      },
      ...(mode !== CrudMode.CREATE
        ? [
            {
              valueType: FormItemValueType.group,
              columns: [
                {
                  title: t('lender.brokers.labels.ohpenId'),
                  dataIndex: 'ohpenId',
                  valueType: FormItemValueType.text,
                  readonly: mode === CrudMode.VIEW,
                  fieldProps: {
                    disabled: true,
                  },
                  colProps: { span: 12 },
                },
              ],
            },
          ]
        : []),
      {
        valueType: FormItemValueType.group,
        columns: [
          {
            title: t('lender.brokers.labels.fcaStatus'),
            dataIndex: 'fcaStatus',
            valueType: FormItemValueType.text,
            readonly,
            fieldProps: {
              ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
              options: selectsOptions[FieldType.USER_STATUS],
              className: getBrokerHighlightProps('fcaStatus', fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('registrationDetails', 'fcaStatus'),
            },
            formItemProps: {
              label: (
                <span>
                  {t('lender.brokers.labels.fcaStatus')}
                  {!!watchedIrn && (
                    <Tag color="blue" className={styles.fcaTag}>
                      {t('firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.brokers.labels.firmName'),
            dataIndex: 'firmId',
            valueType: FormItemValueType.select,
            readonly,
            readonlyDisplay: 'tag',
            fieldProps: {
              options: selectsOptions[FieldType.FIRMS],
              showSearch: true,
              optionFilterProp: 'label',
            },
            formItemProps: {
              rules: [{ required: true }],
              tooltip: readonly ? null : t('lender.brokers.labels.firmChangeTooltip'),
            },
            colProps: { span: 12 },
          },
        ],
      },
    ],
    [
      t,
      readonly,
      selectsOptions,
      loading,
      populateData,
      fcaModifiedFields,
      removeFcaModifiedField,
      form,
      watchedIrn,
      mode,
      roleOptions,
      watchedCognitoUserStatus,
      inviting,
      sendInvitation,
    ],
  );

  const personalColumns: EnsoFormItem[] = useMemo(
    () => [
      {
        title: t('lender.brokers.labels.title'),
        dataIndex: ['personalData', 'title'],
        valueType: FormItemValueType.select,
        fieldProps: {
          options: selectsOptions[FieldType.TITLES],
        },
        readonly,
        colProps: { span: 12 },
      },
      {
        valueType: FormItemValueType.group,
        columns: [
          {
            title: t('lender.brokers.labels.firstName'),
            dataIndex: ['personalData', 'firstName'],
            valueType: FormItemValueType.text,
            readonly,
            fieldProps: {
              ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
              className: getBrokerHighlightProps(['personalData', 'firstName'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('personalDetails', 'firstName'),
            },
            formItemProps: {
              label: (
                <span>
                  {t('lender.brokers.labels.firstName')}
                  {!!watchedIrn && (
                    <Tag color="blue" className={styles.fcaTag}>
                      {t('firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.brokers.labels.lastName'),
            dataIndex: ['personalData', 'lastName'],
            valueType: 'text',
            readonly,
            fieldProps: {
              ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
              className: getBrokerHighlightProps(['personalData', 'lastName'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('personalDetails', 'lastName'),
            },
            formItemProps: {
              label: (
                <span>
                  {t('lender.brokers.labels.lastName')}
                  {!!watchedIrn && (
                    <Tag color="blue" className={styles.fcaTag}>
                      {t('firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: FormItemValueType.group,
        columns: [
          {
            title: t('lender.brokers.labels.dateOfBirth'),
            dataIndex: ['personalData', 'birthDate'],
            valueType: FormItemValueType.date,
            readonly,
            formItemProps: {
              rules: [
                {
                  validator: (_: unknown, value: unknown) => {
                    if (!value) return Promise.resolve();
                    const age = dayjs().diff(dayjs(value as string), 'year');
                    if (age < 18) return Promise.reject(new Error(t('lender.brokers.validation.mustBeAtLeast18')));
                    return Promise.resolve();
                  },
                },
              ],
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: FormItemValueType.group,
        columns: [
          {
            title: t('lender.brokers.labels.previousName'),
            dataIndex: ['personalData', 'previousName'],
            valueType: FormItemValueType.text,
            readonly,
            fieldProps: textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT),
            colProps: { span: 12 },
          },
        ],
      },
    ],
    [readonly, t, fcaModifiedFields, removeFcaModifiedField, watchedIrn, selectsOptions],
  );

  return (
    <>
      <CrudFormCard collapsible={readonly} icon={<IconBuilding />} title={t('lender.brokers.labels.registrationDetails')}>
        <CrudSchemaFields schema={registrationColumns} form={form} readonly={readonly} component={false} />
      </CrudFormCard>
      <CrudFormCard collapsible={readonly} icon={<IconFileText />} title={t('lender.brokers.labels.personalDetails')}>
        <CrudSchemaFields schema={personalColumns} form={form} readonly={readonly} component={false} />
      </CrudFormCard>
    </>
  );
};
